/**
 * Nexus — IA operativa de BarmetHub (BHM-CLD-REQ-042). Cliente de /nexus/*. Fuente de verdad: API.
 */
import { api, ApiError } from '../utils/api';
import { errMsg } from './workApi';

export type ExecStatus = 'QUEUED' | 'RUNNING' | 'WAITING_USER' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
export const EXEC_STATUS_LABELS: Record<ExecStatus, string> = {
  QUEUED: 'En cola', RUNNING: 'Trabajando', WAITING_USER: 'Necesita tu intervención', COMPLETED: 'Resultado listo', FAILED: 'Error', CANCELLED: 'Cancelada',
};
export const EXEC_STATUS_CLS: Record<ExecStatus, string> = {
  QUEUED: 'border-outline-variant text-on-surface-variant',
  RUNNING: 'border-[#3b82f6]/40 bg-[#3b82f6]/10 text-[#60a5fa]',
  WAITING_USER: 'border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#F59E0B]',
  COMPLETED: 'border-[#10B981]/40 bg-[#10B981]/10 text-[#10B981]',
  FAILED: 'border-[#F43F5E]/40 bg-[#F43F5E]/10 text-[#F43F5E]',
  CANCELLED: 'border-outline text-outline',
};
export const CAP_STATUS_LABELS: Record<string, string> = { CONNECTED: 'Conectado', DEGRADED: 'Parcial', DISCONNECTED: 'Sin conectar', UNAVAILABLE: 'No disponible' };

export interface NexusExecution {
  id: string; projectId: string; taskId: string | null; projectName?: string; taskTitle: string | null; request: string; capability: string;
  status: ExecStatus; destination: string | null; result: string | null; requiredActions: string[]; error: string | null; outcome: string | null;
  timeEntryId: string | null; mailMessageId?: string | null; startedAt: string; finishedAt: string | null; closedAt: string | null; elapsedSec: number;
  provider?: string | null; model?: string | null; costUsd?: number | null; inputTokens?: number | null; outputTokens?: number | null; aiDurationMs?: number | null; user?: string;
}
export interface Recommendation {
  taskId: string; title: string; priority: string; status: string; dueDate: string | null; reason: string;
  project: { id: string; name: string; clientName: string | null };
}
export interface NexusContext {
  capabilities: { id: string; label: string; status: string; note?: string }[];
  ai: 'CONNECTED' | 'DEGRADED' | 'UNAVAILABLE';
  providers?: { id: string; label: string; status: string }[];
  permissions: { logTime: boolean; createTask: boolean; admin: boolean };
  mail?: { status: string; configured: boolean; canRead: boolean; canSend: boolean };
  maxParallel: number;
}
export interface NexusDay {
  date: string; clockedIn: boolean; clockedSince: string | null; workedClockSec: number; projectAttributedSec: number; semantics: string;
  activeExecutions: { id: string; status: ExecStatus; projectName: string; taskTitle: string | null; elapsedSec: number }[];
}
export interface Schedule { workStartTime: string | null; workEndTime?: string | null; workDays: number[]; clockedIn: boolean; timezone: string; reminderAfterMin: number }

/** Mensaje de error legible: el interceptor de axios convierte los fallos en ApiError (mensaje del servidor). */
export function nexusErr(err: unknown, fallback = 'No se pudo completar la acción'): string {
  if (err instanceof ApiError && err.message && !/^Cannot (GET|POST|PUT|PATCH|DELETE)/.test(err.message)) return err.message;
  return errMsg(err, fallback);
}

/* ── REQ-045c · Preparar ejecución con Nexus ─────────────────────────── */
export type PrepSourceKind = 'LINK' | 'PATH' | 'REFERENCE';
export interface PrepSource {
  id: string; kind: PrepSourceKind | 'TASK_DESCRIPTION' | 'PROJECT_DESCRIPTION' | 'PROJECT_MEETINGS' | 'LINKED_MAIL' | string;
  label: string; ref: string | null; scope: 'TASK' | 'PROJECT' | 'BARMETHUB' | string; origin: string;
}
export interface PrepMissing { key: string; label: string }
export interface PrepPlan { mode: 'AI' | 'BASIC'; steps: string[]; restrictions: string[]; expectedResult: string; generatedAt: string }
export interface PrepDraft {
  id: string; status: string; objective: string; instructions: string | null; clarification: string | null; plan: PrepPlan | null;
  missing: PrepMissing[]; missingWaived: boolean; question: string | null; questionAnswer: string | null; executionId: string | null;
  confirmedAt: string | null; cancelledAt: string | null; createdAt: string; updatedAt: string; canConfirm: boolean;
}
export type PrepSectionState = 'INCLUDED' | 'NO_DATA' | 'DENIED';
export interface PrepView {
  task: { id: string; title: string; status: string; priority: string };
  project: { id: string; name: string; clientName: string | null };
  objective: { title: string; detail: string | null };
  how: { instructions: { origin: 'TASK' | 'PROJECT'; label: string; text: string }[]; hasInstructions: boolean };
  unavailableFields: { key: string; label: string }[];
  fichaContext: {
    sections: { key: string; label: string; state: PrepSectionState; items: number }[];
    obligation: { name: string; process: string | null; expectedResult: string | null; periodicity: string } | null;
  } | null;
  sources: { available: PrepSource[]; unavailable: { key: string; label: string; reason: string }[] };
  pendingProposals: { id: string; kind: string; label: string; ref: string | null; createdBy: string }[];
  canApproveProposals: boolean;
  draft: PrepDraft | null;
}
export interface PrepHistoryItem extends PrepDraft {
  taskId: string; projectId: string; user: { id: string; name: string | null }; sources: PrepSource[];
  execution: { id: string; status: ExecStatus; outcome: string | null; finishedAt: string | null; closedAt: string | null; hasResult: boolean; timeEntryId: string | null } | null;
}

const g = async <T,>(url: string, params?: Record<string, unknown>) => (await api.get<T>(url, { params })).data;

export const nexusApi = {
  context: () => g<NexusContext>('/nexus/context'),
  recommendations: () => g<Recommendation[]>('/nexus/recommendations'),
  day: () => g<NexusDay>('/nexus/day'),
  clockIn: async () => (await api.post<{ recommendations: Recommendation[] }>('/nexus/clock-in')).data,
  executions: () => g<NexusExecution[]>('/nexus/executions'),
  execution: (id: string) => g<NexusExecution>(`/nexus/executions/${id}`),
  create: async (d: { projectId?: string; taskId?: string; request?: string; mailMessageId?: string }) => (await api.post<NexusExecution>('/nexus/executions', d)).data,
  batch: async (taskIds: string[]) => (await api.post<NexusExecution[]>('/nexus/executions/batch', { taskIds })).data,
  cancel: async (id: string) => (await api.post<NexusExecution>(`/nexus/executions/${id}/cancel`)).data,
  finalize: async (id: string, outcome: 'DONE' | 'REVIEW' | 'CLOSE', logTime = true) =>
    (await api.post<{ execution: NexusExecution; next: Recommendation[] }>(`/nexus/executions/${id}/finalize`, { outcome, logTime })).data,
  followupTask: async (id: string, title: string, processId?: string) => (await api.post(`/nexus/executions/${id}/followup-task`, { title, ...(processId ? { processId } : {}) })).data,
  activity: async (events: { type: string; module?: string; projectId?: string; taskId?: string }[]) => (await api.post('/nexus/activity', { events })).data,
  schedule: () => g<Schedule>('/nexus/schedule'),
  setSchedule: async (userId: string, d: { workStartTime: string | null; workEndTime?: string | null; workDays?: number[] }) => (await api.patch(`/nexus/schedule/${userId}`, d)).data,
  preparation: (projectId: string, taskId: string) => g<PrepView>('/nexus/preparation', { projectId, taskId }),
  preparationHistory: (taskId?: string) => g<PrepHistoryItem[]>('/nexus/preparation/history', taskId ? { taskId } : undefined),
  saveClarification: async (d: { projectId: string; taskId: string; clarification: string }) => (await api.put<PrepDraft>('/nexus/preparation/clarification', d)).data,
  addSource: async (d: { projectId: string; taskId: string; kind: PrepSourceKind; label?: string; ref: string }) => (await api.post<PrepSource>('/nexus/preparation/sources', d)).data,
  removeSource: async (id: string) => (await api.delete<{ ok: boolean }>(`/nexus/preparation/sources/${id}`)).data,
  proposeSource: async (id: string) => (await api.post<{ id: string; status: string; note: string }>(`/nexus/preparation/sources/${id}/propose`)).data,
  decideSource: async (id: string, approve: boolean) => (await api.post<{ id: string; status: string }>(`/nexus/preparation/sources/${id}/decision`, { approve })).data,
  plan: async (d: { projectId: string; taskId: string; clarification?: string; answer?: string; waiveMissing?: boolean }) => (await api.post<PrepDraft>('/nexus/preparation/plan', d)).data,
  confirmPreparation: async (id: string) => (await api.post<{ execution: NexusExecution; preparation: PrepDraft }>(`/nexus/preparation/${id}/confirm`)).data,
  cancelPreparation: async (id: string) => (await api.post<PrepDraft>(`/nexus/preparation/${id}/cancel`)).data,
  admin: (q: Record<string, string> = {}) => g<{ totals: { executions: number; failed: number; costUsdKnown: number; costUnknown: number }; executions: NexusExecution[] }>('/nexus/admin/executions', q),
};

export const fmtDur = (sec: number) => {
  const h = Math.floor(sec / 3600); const m = Math.floor((sec % 3600) / 60);
  return h ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`;
};
